import {
  DestroyRef,
  DOCUMENT,
  EnvironmentProviders,
  PLATFORM_ID,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Auth, beforeAuthStateChanged, idToken } from '@angular/fire/auth';

/**
 * The cookie that carries the signed-in user's Firebase ID token to the
 * server, which renders the page as that user. Firebase Hosting forwards no
 * other cookie name to server code.
 */
export const SESSION_COOKIE = '__session';

/** The request context `server.ts` passes to every server render. */
export interface ServerRenderContext {
  authIdToken?: string;
}

/**
 * The ID token of a server render's visitor, if they are signed in. A cookie
 * that is not an unexpired JWT renders signed out: `initializeServerApp`
 * throws on one it cannot parse, and only logs an expired one.
 */
export function serverRenderAuthIdToken(
  context: unknown,
  now = Date.now(),
): string | undefined {
  if (typeof context !== 'object' || context === null) return undefined;
  const token = (context as ServerRenderContext).authIdToken;
  if (typeof token !== 'string') return undefined;
  const exp = jwtExpiry(token);
  return exp !== undefined && exp * 1000 > now ? token : undefined;
}

function jwtExpiry(token: string): number | undefined {
  const parts = token.split('.');
  if (parts.length !== 3) return undefined;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload: unknown = JSON.parse(atob(base64));
    const exp = (payload as { exp?: unknown } | null)?.exp;
    return typeof exp === 'number' ? exp : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Keeps the {@link SESSION_COOKIE} in step with the browser's auth state: on
 * every sign-in, token refresh and sign-out, and before a sign-out completes,
 * so a page load that races it cannot render the previous user.
 */
export function provideSessionCookie(): EnvironmentProviders {
  return provideAppInitializer(() => {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) return;
    const auth = inject(Auth);
    const document = inject(DOCUMENT);
    const destroyRef = inject(DestroyRef);

    const write = (token: string | null | undefined): void => {
      document.cookie = token
        ? `${SESSION_COOKIE}=${token}; Path=/; Secure; SameSite=Lax`
        : `${SESSION_COOKIE}=; Path=/; Max-Age=0; Secure; SameSite=Lax`;
    };
    const read = (): string | undefined =>
      document.cookie
        .split('; ')
        .find((cookie) => cookie.startsWith(`${SESSION_COOKIE}=`))
        ?.slice(SESSION_COOKIE.length + 1) || undefined;

    idToken(auth).pipe(takeUntilDestroyed(destroyRef)).subscribe(write);

    let priorToken: string | undefined;
    const unsubscribe = beforeAuthStateChanged(
      auth,
      async (user) => {
        priorToken = read();
        write(await user?.getIdToken());
      },
      () => write(priorToken),
    );
    destroyRef.onDestroy(unsubscribe);
  });
}
